import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import '@fontsource/cinzel/700.css'
import '@fontsource/cinzel/900.css'
import '@fontsource/great-vibes/400.css'
import { founderRoles } from './founderRoles'
import type { FounderRole } from './founderRoles'

// Frame painted by Codex with no text (design/codex/prompt-cert-frame.md); every word is drawn here,
// so the page and the downloaded PNG are the same picture.
const FRAME = '/sections/certificate-frame.webp'
const W = 1200
const H = 1800

// Vertical positions as share of the height, measured on the Codex frame (design/codex/cert-frame-a.png).
const LAYOUT = {
  crest: 0.149, motto: 0.26, title: 0.356, subtitle: 0.397, banner: 0.442, name: 0.542,
  body1: 0.614, body2: 0.64, rank: 0.715, label: 0.79, value: 0.82, medal: 0.802, id: 0.958,
}
const LEFT = 0.234
const RIGHT = 0.767
const INK = '#123d2b'
const GOLD = '#a8781f'
const PALE_GOLD = '#f3dc97'

export type CertificateHandle = { toBlob: () => Promise<Blob> }

const NAME_MAX = 24

// Visitor names never leave the browser and are only painted on the canvas, so no markup can run;
// this just keeps them printable and short enough to fit the script line.
export function cleanName(value: string) {
  const printable = value.replace(/[\p{Cc}\p{Cf}]/gu, '').replace(/\s+/g, ' ').trimStart()
  return Array.from(printable).slice(0, NAME_MAX).join('')
}

let framePromise: Promise<HTMLImageElement> | null = null
function loadFrame() {
  framePromise ??= new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => { framePromise = null; reject(new Error('Certificate frame failed to load.')) }
    img.src = FRAME
  })
  return framePromise
}

function text(ctx: CanvasRenderingContext2D, value: string, y: number, font: string, color: string | CanvasGradient, spacing = 0, maxWidth = W * 0.7, x = 0.5) {
  ctx.font = font
  ctx.fillStyle = color
  ctx.letterSpacing = `${spacing}px`
  // Shrink long values (e.g. "Serial Almost-Founder") until they fit.
  let size = parseFloat(font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? '40')
  while (ctx.measureText(value).width > maxWidth && size > 10) {
    size -= 2
    ctx.font = font.replace(/\d+(?:\.\d+)?px/, `${size}px`)
  }
  ctx.fillText(value, W * x, H * y)
}

function goldFoil(ctx: CanvasRenderingContext2D, y: number, height: number) {
  const g = ctx.createLinearGradient(0, H * y - height, 0, H * y + height * 0.3)
  g.addColorStop(0, '#e9c86b')
  g.addColorStop(0.5, '#b8862a')
  g.addColorStop(1, '#8a5f14')
  return g
}

export async function drawCertificate(canvas: HTMLCanvasElement, role: FounderRole, name = '') {
  await Promise.all(['700 40px Cinzel', '900 40px Cinzel', '400 40px "Great Vibes"'].map(f => document.fonts.load(f)))
  const frame = await loadFrame()
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Your browser cannot draw this certificate.')
  ctx.drawImage(frame, 0, 0, W, H)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'

  const L = LAYOUT
  const index = Math.max(0, founderRoles.findIndex(r => r.id === role.id))
  const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

  text(ctx, 'LARPup', L.crest, '700 46px Cinzel', PALE_GOLD, 1, W * 0.17)
  text(ctx, 'HONORING AUDACITY · INSPIRING NOTHING', L.motto, '700 19px Cinzel', INK, 4, W * 0.58)
  text(ctx, 'CERTIFICATE', L.title, '900 120px Cinzel', INK, 4, W * 0.76)
  text(ctx, 'OF PROFESSIONAL LARPING', L.subtitle, '700 32px Cinzel', GOLD, 6, W * 0.56)
  text(ctx, 'THIS CERTIFICATE IS AWARDED TO', L.banner, '700 26px Cinzel', PALE_GOLD, 4, W * 0.52)
  const holder = cleanName(name).trim()
  text(ctx, holder || role.label, L.name, '400 132px "Great Vibes"', goldFoil(ctx, L.name, 110), 0, W * 0.7)
  if (holder) text(ctx, role.label.toUpperCase(), L.name + 0.03, '700 22px Cinzel', GOLD, 5, W * 0.5)
  text(ctx, 'In recognition of exceptional self-appointment,', L.body1, 'italic 31px Georgia, serif', '#3b4a40', 0, W * 0.74)
  text(ctx, 'unstoppable confidence and zero verifiable achievements.', L.body2, 'italic 31px Georgia, serif', '#3b4a40', 0, W * 0.74)
  text(ctx, 'RANKED #1 IN IMAGINATION', L.rank, '700 24px Cinzel', GOLD, 5, W * 0.45)

  text(ctx, 'DATE', L.label, '700 22px Cinzel', INK, 4, W * 0.2, LEFT)
  text(ctx, date, L.value, 'italic 30px Georgia, serif', INK, 0, W * 0.22, LEFT)
  text(ctx, 'SIGNATURE', L.label, '700 22px Cinzel', INK, 4, W * 0.2, RIGHT)
  text(ctx, 'C. Vibes', L.value + 0.004, '400 58px "Great Vibes"', INK, 0, W * 0.2, RIGHT)
  text(ctx, 'CHIEF VIBES OFFICER', L.value + 0.024, '700 15px Cinzel', '#3b4a40', 2, W * 0.2, RIGHT)

  ctx.shadowColor = '#fff3c480'
  ctx.shadowOffsetY = 1
  text(ctx, '100%', L.medal + 0.002, '900 46px Cinzel', '#3a2504', 0, W * 0.13)
  text(ctx, 'SELF-APPOINTED', L.medal + 0.02, '900 17px Cinzel', '#3a2504', 1, W * 0.15)
  ctx.shadowColor = 'transparent'
  text(ctx, `ID LARP-2026-${String(42 + index * 137).padStart(5, '0')} · NOT A REAL QUALIFICATION`, L.id, '700 15px Cinzel', '#4a5a50', 2, W * 0.44)
}

const Certificate = forwardRef<CertificateHandle, { role: FounderRole; name?: string }>(function Certificate({ role, name = '' }, ref) {
  const canvas = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (canvas.current) drawCertificate(canvas.current, role, name).catch(() => {})
  }, [role, name])

  useImperativeHandle(ref, () => ({
    toBlob: async () => {
      const out = document.createElement('canvas')
      await drawCertificate(out, role, name)
      return new Promise<Blob>((resolve, reject) => out.toBlob(b => (b ? resolve(b) : reject(new Error('The image could not be saved.'))), 'image/png'))
    },
  }), [role, name])

  return <canvas ref={canvas} className="certificate" width={W} height={H} role="img" aria-label={`Certificate of Professional LARPing awarded to ${cleanName(name).trim() || role.label}${cleanName(name).trim() ? `, ${role.label}` : ''}. Ranked number one in imagination. Not a real qualification.`} />
})

export default Certificate

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 10000)
}
